import { AsyncPipe } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { AgencyFullDto, AgencyMeControllerService } from '@crd-api/api';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faArrowUpRightFromSquare,
  faBookOpen,
  faChevronRight,
  faComments,
  faKey,
  faMobileScreen,
  faRocket,
  faShieldHalved,
  faUsers,
} from '@fortawesome/free-solid-svg-icons';
import { Observable, catchError, map, of } from 'rxjs';
import { AuthenticationService } from '../../../security/authentication.service';

interface HomeActionCard {
  buttonClass: string;
  buttonLabel: string;
  description: string;
  icon: IconDefinition;
  route: string;
  title: string;
  tone: 'primary' | 'success';
}

interface HomeResourceCard {
  description: string;
  icon: IconDefinition;
  links: HomeResourceLink[];
  title: string;
  tone: 'blue' | 'green' | 'gold';
}

type HomeResourceLink =
  | { label: string; route: string; url?: never }
  | { label: string; route?: never; url: string };

interface HomeQuickLaunchLink {
  badge: string;
  description: string;
  route: string;
  title: string;
}

interface HomeAccountStatusNotice {
  description: string;
  linkLabel: string;
  route: string;
  title: string;
  tone: 'setup' | 'status';
}

@Component({
  selector: 'app-welcome',
  templateUrl: './welcome.component.html',
  styleUrls: ['./welcome.component.scss'],
  imports: [
    AsyncPipe,
    FontAwesomeModule,
    RouterLink
  ],
  standalone: true
})
export class WelcomeComponent implements OnInit {
  private static readonly APPROVED_STATUS = 'Approved';
  private static readonly FIRST_TIME_SIGN_IN_INSTRUCTIONS_URL =
    'https://www.sandiegocounty.gov/probation/docs/CRD_First-Time_Sign-In_Instructions.pdf';
  private static readonly NOT_SUBMITTED_STATUS = 'Not Submitted';

  authenticationService = inject(AuthenticationService);
  private readonly agencyMeControllerService = inject(AgencyMeControllerService);

  protected readonly faArrowUpRightFromSquare = faArrowUpRightFromSquare;
  protected readonly faChevronRight = faChevronRight;
  protected readonly faMobileScreen = faMobileScreen;
  protected readonly faShieldHalved = faShieldHalved;
  protected readonly firstTimeSignInInstructionsUrl = WelcomeComponent.FIRST_TIME_SIGN_IN_INSTRUCTIONS_URL;
  protected accountStatusNotice$: Observable<HomeAccountStatusNotice | null> = of(null);

  protected readonly actionCards: HomeActionCard[] = [
    {
      buttonClass: 'btn-primary',
      buttonLabel: 'Login',
      description: 'Sign in to manage agency information, users, programs, reporting, and referrals.',
      icon: faKey,
      route: '/login',
      title: 'Already registered?',
      tone: 'primary'
    },
    {
      buttonClass: 'btn-success',
      buttonLabel: 'Getting Started',
      description: 'New providers can review requirements and begin the registration process for agency participation.',
      icon: faRocket,
      route: '/getting-started',
      title: 'Start registration',
      tone: 'success'
    }
  ];

  protected readonly resourceCards: HomeResourceCard[] = [
    {
      description: 'Learn how the directory works, review participation information, and find the steps for getting started.',
      icon: faBookOpen,
      links: [
        { label: 'About the Program', route: '/about' },
        { label: 'Getting Started', route: '/getting-started' }
      ],
      title: 'Support',
      tone: 'blue'
    },
    {
      description: 'Find guidance for setting up multiple users and adding or maintaining program information.',
      icon: faUsers,
      links: [
        { label: 'Multiple Users Setup', route: '/help-user' },
        { label: 'Adding New Programs', route: '/help-program' }
      ],
      title: 'Help',
      tone: 'green'
    },
    {
      description: 'Browse common questions or send a message to the Probation support team for help.',
      icon: faComments,
      links: [
        { label: 'Frequently Asked Questions', route: '/faq' },
        { label: 'Contact Us', route: '/contact-us' }
      ],
      title: 'Questions?',
      tone: 'gold'
    }
  ];

  protected readonly quickLaunchLinks: HomeQuickLaunchLink[] = [
    {
      badge: 'AV',
      description: 'Review referrals.',
      route: '/all-referrals-view',
      title: 'All Referrals View'
    },
    {
      badge: 'WR',
      description: 'Complete weekly referral updates.',
      route: '/weekly-reporting',
      title: 'Weekly Referral Reporting'
    },
    {
      badge: 'PR',
      description: 'View a specific probationer.',
      route: '/access-probationer',
      title: 'Access Probationer Record'
    }
  ];

  protected get isAuthenticated(): boolean {
    return this.authenticationService.isAuthenticated();
  }

  protected get hasHeroAside(): boolean {
    return !this.isAuthenticated || this.showReferralQuickLaunch;
  }

  protected get showReferralQuickLaunch(): boolean {
    return this.isAuthenticated && this.authenticationService.hasRole('ROLE_ReferralsScreens');
  }

  protected get returningTitle(): string {
    return this.showReferralQuickLaunch
      ? 'Welcome back to the Community Resource Directory.'
      : 'Welcome back to the Community Resource Directory.';
  }

  protected get returningDescription(): string {
    return this.showReferralQuickLaunch
      ? 'Access approved community-based resources, manage agency information, and stay connected with probation officers as we work together to support rehabilitation and reduce recidivism.'
      : 'Use this site to continue your work, manage provider information, and find support resources for account and program updates.';
  }

  ngOnInit(): void {
    this.accountStatusNotice$ = this.loadAccountStatusNotice();
  }

  private loadAccountStatusNotice(): Observable<HomeAccountStatusNotice | null> {
    if (!this.isAuthenticated) {
      return of(null);
    }

    return this.agencyMeControllerService.getAgencyFullDtoByCurrentAgency().pipe(
      map(currentAgency => this.buildAccountStatusNotice(currentAgency)),
      catchError(error => {
        console.error('Error loading agency account status:', error);
        return of(null);
      })
    );
  }

  private buildAccountStatusNotice(currentAgency: AgencyFullDto): HomeAccountStatusNotice | null {
    const accountStatus = currentAgency.agencyStatus?.status ?? currentAgency.agencyStatus?.id ?? null;

    if (!accountStatus || accountStatus === WelcomeComponent.APPROVED_STATUS) {
      return null;
    }

    if (accountStatus === WelcomeComponent.NOT_SUBMITTED_STATUS) {
      return {
        description: 'Your account has not been submitted yet. Complete Account Setup so Probation can review your agency information.',
        linkLabel: 'Go to Account Setup',
        route: '/agency-wizard',
        title: 'Complete Account Setup',
        tone: 'setup'
      };
    }

    return {
      description: `Your account is currently ${accountStatus}. Review Account Status for instructions and any next steps needed before your account is approved.`,
      linkLabel: 'View Account Status',
      route: '/status',
      title: 'Review Account Status',
      tone: 'status'
    };
  }
}
